import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown } from "lucide-react";
import { clsx } from "clsx";

const FAQS = [
  {
    q: "What exactly is AgentsToGo?",
    a: "AgentsToGo is a done-for-you AI agent building service. You describe what you need automated, and we build a custom AI agent tailored to your business — fully deployed and ready to use.",
  },
  {
    q: "Is there really a 100% money-back guarantee?",
    a: "Yes. If we can't deliver a working agent that meets the agreed scope, you get a full refund — no questions asked.",
  },
  {
    q: "What kinds of agents can you build?",
    a: "We build agents for automation, customer support, lead enrichment, content moderation, data processing, Discord bots, Slack assistants, invoice processing, and much more. If it can be automated with AI, we can build it.",
  },
  {
    q: "What tools and platforms do you integrate with?",
    a: "We integrate with the tools you already use — Slack, Discord, Notion, Airtable, Google Sheets, Zapier, n8n, OpenAI, and many others. Just let us know your stack.",
  },
  {
    q: "How long does it take to build my agent?",
    a: "Most agents are designed, built, and delivered within 5–10 business days after the discovery call, depending on complexity.",
  },
  {
    q: "Do I need any technical knowledge?",
    a: "None at all. You describe the problem in plain language and we handle everything — architecture, development, testing, and deployment.",
  },
  {
    q: "Who owns the agent once it's built?",
    a: "You do. All deliverables, code, and configurations are yours. We hand everything over at the end of the engagement.",
  },
  {
    q: "What happens after the agent is delivered?",
    a: "We offer a support window after delivery to handle any fine-tuning. For ongoing changes and maintenance, we have retainer packages available.",
  },
  {
    q: "How do I get started?",
    a: "Just chat with us here on the homepage. Describe what you need and our AI will guide you through the discovery process. Once we understand your use case, you can book your kickoff call directly.",
  },
];

interface FAQModalProps {
  open: boolean;
  onClose: () => void;
}

export function FAQModal({ open, onClose }: FAQModalProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="faq-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            key="faq-panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative scifi-wave-bg rounded-2xl w-full max-w-lg flex flex-col overflow-hidden border border-cyan-400/25"
            style={{
              maxHeight: "82vh",
              boxShadow: "0 24px 80px rgba(0,0,0,0.55), 0 0 40px rgba(0,229,255,0.12)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 shrink-0 border-b border-cyan-400/15 bg-black/30">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-[15px] font-semibold text-white tracking-tight">
                    Frequently asked questions
                  </h2>
                  <p className="text-xs text-cyan-300 mt-0.5">Everything you need to know</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded-full text-slate-400 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors mt-0.5"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* FAQ list */}
            <div className="overflow-y-auto flex-1 divide-y divide-cyan-400/10 bg-black/30">
              {FAQS.map((faq, i) => (
                <div key={i}>
                  <button
                    className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left group"
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  >
                    <span className={clsx(
                      "text-sm leading-snug transition-colors",
                      openIndex === i ? "text-cyan-300 font-medium" : "text-slate-200 group-hover:text-white"
                    )}>
                      {faq.q}
                    </span>
                    <ChevronDown
                      className={clsx(
                        "w-4 h-4 shrink-0 transition-transform duration-200",
                        openIndex === i ? "rotate-180 text-cyan-300" : "text-slate-500"
                      )}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openIndex === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-4 text-sm text-slate-300 leading-relaxed">
                          {faq.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
